<template>
  <canvas ref="c" class="viz" :class="stateClass"></canvas>
</template>

<script>
export default {
  name: "VoiceVisualizerNew",
  props: {
    // New explicit states (keep 'active' for backward compat)
    listening: { type: Boolean, default: false },
    responding: { type: Boolean, default: false },
    thinking: { type: Boolean, default: false },
    active: { type: Boolean, default: false }, // fallback to old prop
  },
  data() {
    return {
      ctx: null,
      raf: 0,
      t: 0,
      // optional audio analysis
      audioCtx: null,
      analyser: null,
      dataArray: null,
      source: null,
      triedSetup: false,
    };
  },
  computed: {
    // priority: listening > responding > thinking > idle
    mode() {
      if (this.listening || this.active) return "listening";
      if (this.responding) return "responding";
      if (this.thinking) return "thinking";
      return "idle";
    },
    stateClass() {
      return `state-${this.mode}`;
    },
  },
  mounted() {
    const c = this.$refs.c;
    c.width = c.clientWidth;
    c.height = c.clientHeight;
    this.ctx = c.getContext("2d");

    // Try to attach to an existing mic stream if the app has one
    // (AIDashboard can set: window.__cogneetivMicStream = mediaStream)
    if (window.__cogneetivMicStream) {
      this.setupAnalyser(window.__cogneetivMicStream);
    }

    // Listen once if some other component provides the stream later
    window.addEventListener("cog:mic-stream", this.onMicStream, { once: true });

    this.loop();
    window.addEventListener("resize", this.resize);
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.resize);
    window.removeEventListener("cog:mic-stream", this.onMicStream);
    try {
      if (this.source) this.source.disconnect();
      if (this.analyser) this.analyser.disconnect();
      // don't close shared audioCtx; it may be used elsewhere
    } catch (e) {
      console.warn("Visualizer cleanup warning:", e);
    }
  },
  methods: {
    onMicStream(e) {
      const stream = e && e.detail;
      if (stream) this.setupAnalyser(stream);
    },
    setupAnalyser(stream) {
      try {
        if (!stream || this.analyser) return;
        this.audioCtx = this.audioCtx || new (window.AudioContext || window.webkitAudioContext)();
        this.source = this.audioCtx.createMediaStreamSource(stream);
        this.analyser = this.audioCtx.createAnalyser();
        this.analyser.fftSize = 1024;
        this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
        this.source.connect(this.analyser);
      } catch (e) {
        console.warn("Could not setup analyser; falling back to synthetic levels.", e);
      }
    },
    resize() {
      const c = this.$refs.c;
      c.width = c.clientWidth;
      c.height = c.clientHeight;
    },
    sampleLevel() {
      // Real mic level if we have an analyser and we're in listening mode
      if (this.mode === "listening" && this.analyser && this.dataArray) {
        this.analyser.getByteTimeDomainData(this.dataArray);
        // RMS around the 128 baseline (0..255)
        let sum = 0;
        for (let i = 0; i < this.dataArray.length; i++) {
          const v = (this.dataArray[i] - 128) / 128; // -1..1
          sum += v * v;
        }
        const rms = Math.sqrt(sum / this.dataArray.length); // 0..~1
        return Math.min(1, rms * 2.2); // little gain
      }
      // Synthetic level for other states
      this.t += 0.035;
      const n = (Math.sin(this.t) + Math.sin(this.t * 0.73 + 1.7)) * 0.5 + 0.5; // 0..1
      switch (this.mode) {
        case "responding": return 0.35 + n * 0.35; // medium energy
        case "thinking":   return 0.15 + n * 0.18; // subtle
        case "idle":       return 0.05 + n * 0.08; // very subtle
        default:           return 0.45 + n * 0.45; // listening (synthetic fallback)
      }
    },
    barGradient(ctx, w, h) {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      if (this.mode === "listening") {
        g.addColorStop(0, "#00e0ff");
        g.addColorStop(1, "#7c6cff");
      } else if (this.mode === "responding") {
        g.addColorStop(0, "#ff6bd6");
        g.addColorStop(1, "#b36bff");
      } else if (this.mode === "thinking") {
        g.addColorStop(0, "#ffd36b");
        g.addColorStop(1, "#ffb36b");
      } else {
        g.addColorStop(0, "rgba(255,255,255,0.55)");
        g.addColorStop(1, "rgba(255,255,255,0.25)");
      }
      return g;
    },
    glowColor() {
      if (this.mode === "listening") return "rgba(0,224,255,0.55)";
      if (this.mode === "responding") return "rgba(255,107,214,0.55)";
      if (this.mode === "thinking") return "rgba(255,196,107,0.45)";
      return "rgba(255,255,255,0.25)";
    },
    loop() {
      const { ctx } = this;
      if (!ctx) return;

      // Try once to attach to mic if we just switched to listening
      if (!this.analyser && (this.listening || this.active) && !this.triedSetup) {
        this.triedSetup = true;
        if (window.__cogneetivMicStream) {
          this.setupAnalyser(window.__cogneetivMicStream);
        }
      }

      const w = ctx.canvas.width;
      const h = ctx.canvas.height;
      ctx.clearRect(0, 0, w, h);

      // background subtle frosted band
      ctx.fillStyle = "rgba(255,255,255,0.035)";
      ctx.fillRect(0, 0, w, h);

      // draw animated bars
      const bars = Math.max(24, Math.floor(w / 18));
      const mid = h / 2;
      const grad = this.barGradient(ctx, w, h);
      const glow = this.glowColor();
      const level = this.sampleLevel(); // 0..1
      const baseHeight = h * (0.15 + level * 0.75);

      ctx.shadowColor = glow;
      ctx.shadowBlur = 18;

      for (let i = 0; i < bars; i++) {
        const x = (i + 0.5) * (w / bars);
        // per-bar phase offset for wave look
        const phase = (i * 0.22) + (this.t * 2.0);
        const wave = Math.sin(phase) * 0.5 + 0.5; // 0..1
        const height = Math.max(6, baseHeight * (0.5 + wave * 0.9));
        ctx.fillStyle = grad;
        ctx.fillRect(x - 3, mid - height / 2, 6, height);
      }

      // top “scanline” to make it pop when active
      if (this.mode !== "idle") {
        ctx.shadowBlur = 0;
        ctx.fillStyle = glow;
        const scanY = mid - baseHeight / 2 + (Math.sin(this.t * 1.6) * h * 0.08);
        ctx.fillRect(0, scanY, w, 2);
      }

      this.raf = requestAnimationFrame(this.loop);
    },
  },
};
</script>

<style scoped>
.viz {
  width: 100%;
  height: 100px;
  display: block;
  opacity: .98;
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,.02));
  border: 1px solid rgba(255,255,255,.08);
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.02);
  transition: box-shadow .2s ease, filter .2s ease;
}

/* Subtle state glows (extra hint) */
.viz.state-listening { box-shadow: 0 0 24px rgba(0,224,255,.18), inset 0 0 0 1px rgba(0,224,255,.10); }
.viz.state-responding { box-shadow: 0 0 24px rgba(255,107,214,.18), inset 0 0 0 1px rgba(255,107,214,.10); }
.viz.state-thinking { box-shadow: 0 0 24px rgba(255,196,107,.16), inset 0 0 0 1px rgba(255,196,107,.10); }
</style>
